import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  EntitySelectComponent,
  EntitySelectPage
} from '../../../../../../../shared/components/entity-select/entity-select.component';
import {
  UserDirectoryApiService,
  DirectoryUser,
  directoryUserLabel
} from '../../../../../../../shared/services/user-directory-api.service';

/** Formato de correo aceptado en cliente (mismo criterio pragmático que valida el backend). */
export const WORKFLOW_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

interface RecipientChip {
  key: string;
  kind: 'USER' | 'EMAIL';
  /** ACTIVE = normal · INACTIVE = usuario dado de baja · MISSING = el directorio no lo devolvió. */
  state: 'ACTIVE' | 'INACTIVE' | 'MISSING';
  label: string;
  sublabel: string;
  note: string;
  userId: number | null;
  email: string | null;
}

/**
 * Selector de destinatarios combinables de una acción de email del builder: usuarios del CRM
 * (chips resueltos por nombre) + correos sueltos (chips validados en cliente).
 *
 * Se reutiliza en "Para", "CC" y "CCO". El "contacto del registro" NO vive aquí porque solo
 * aplica a "Para": lo controla el componente padre con su checkbox.
 *
 * Los correos se validan ANTES de añadirlos: el backend los descartaría con un warning que el
 * usuario no vería hasta ejecutar el workflow.
 */
@Component({
  selector: 'app-wf-recipient-picker',
  standalone: true,
  imports: [CommonModule, EntitySelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="space-y-2">
      @if (chips().length) {
        <div class="flex flex-wrap gap-1.5">
          @for (chip of chips(); track chip.key) {
            <span [ngClass]="chipClass(chip)" [title]="chipTitle(chip)">
              <i class="fa-solid text-[10px] opacity-70" [ngClass]="chipIcon(chip)"></i>
              <span class="max-w-[11rem] truncate">{{ chip.label }}</span>
              @if (chip.note) {
                <span class="shrink-0 text-[10px] font-medium uppercase opacity-80">{{ chip.note }}</span>
              }
              @if (!disabled()) {
                <button type="button" class="opacity-60 transition hover:opacity-100" (click)="remove(chip)"
                        [attr.aria-label]="'Quitar ' + chip.label" title="Quitar destinatario">
                  <i class="fa-solid fa-xmark text-[10px]"></i>
                </button>
              }
            </span>
          }
        </div>
      }

      @if (allowUsers()) {
        <div>
          <!-- El slot se recrea tras cada alta para vaciar el typeahead (el buscador conserva el texto elegido). -->
          @for (slot of selectorSlots(); track slot) {
            <app-entity-select
              [value]="null" [displayText]="''" [allowClear]="false" [disabled]="disabled()"
              [searchFn]="userSearch" [labelFn]="userLabel" [sublabelFn]="userSublabel"
              [placeholder]="userPlaceholder()"
              (selected)="onUserSelected($event)"></app-entity-select>
          }
        </div>
      }

      <div class="flex items-start gap-2">
        <div class="flex-1">
          <input type="email" [class]="fieldClass" [value]="emailDraft()" [disabled]="disabled()"
                 [placeholder]="emailPlaceholder()"
                 (input)="onEmailDraftInput($event)" (keydown.enter)="onEmailEnter($event)" />
        </div>
        <button type="button"
                class="shrink-0 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
                [disabled]="disabled() || !emailDraft().trim()" (click)="addEmails()">
          Añadir
        </button>
      </div>

      @if (emailError()) {
        <p class="text-[11px] text-error-500">{{ emailError() }}</p>
      }
    </div>
  `
})
export class WorkflowRecipientPickerComponent {
  private readonly directory = inject(UserDirectoryApiService);

  readonly userIds = input<number[]>([]);
  readonly emails = input<string[]>([]);
  readonly disabled = input<boolean>(false);
  /** "Para" de marketing no admite usuarios; el resto sí. */
  readonly allowUsers = input<boolean>(true);
  readonly userPlaceholder = input<string>('Buscar usuario del CRM por nombre o correo…');
  readonly emailPlaceholder = input<string>('correo@dominio.com');

  readonly userIdsChange = output<number[]>();
  readonly emailsChange = output<string[]>();

  protected readonly emailDraft = signal('');
  protected readonly emailError = signal('');
  private readonly epoch = signal(0);
  protected readonly selectorSlots = computed(() => [this.epoch()]);

  protected readonly fieldClass =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90';
  protected readonly userChipClass =
    'inline-flex max-w-full items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-2 py-1 text-[11px] text-brand-700 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-300';
  protected readonly inactiveChipClass =
    'inline-flex max-w-full items-center gap-1.5 rounded-full border border-warning-200 bg-warning-50 px-2 py-1 text-[11px] text-warning-700 dark:border-warning-500/40 dark:bg-warning-500/10 dark:text-warning-400';
  protected readonly missingChipClass =
    'inline-flex max-w-full items-center gap-1.5 rounded-full border border-dashed border-gray-300 bg-gray-50 px-2 py-1 text-[11px] text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400';
  protected readonly emailChipClass =
    'inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300';

  /**
   * Chips = usuarios (nombre resuelto por la caché del directorio) + correos sueltos.
   *
   * Un usuario dado de baja se muestra DISTINGUIBLE, no se oculta: el nodo lo sigue
   * referenciando y es peor no ver nada. Si el directorio no devuelve el id (eliminado o de
   * otra empresa) se degrada a «Usuario #id · no disponible».
   */
  protected readonly chips = computed<RecipientChip[]>(() => {
    const known = this.directory.knownUsers();
    const missing = this.directory.unresolvedUserIds();
    const users = this.userIds().map<RecipientChip>((id) => {
      const user = known.get(id);
      const state: RecipientChip['state'] = user
        ? user.isActive === false
          ? 'INACTIVE'
          : 'ACTIVE'
        : missing.has(id)
          ? 'MISSING'
          : 'ACTIVE';
      return {
        key: `u-${id}`,
        kind: 'USER',
        state,
        label: user ? directoryUserLabel(user) : `Usuario #${id}`,
        sublabel: user?.mail ?? '',
        note: state === 'INACTIVE' ? 'inactivo' : state === 'MISSING' ? 'no disponible' : '',
        userId: id,
        email: null
      };
    });
    const emails = this.emails().map<RecipientChip>((email) => ({
      key: `e-${email.toLowerCase()}`,
      kind: 'EMAIL',
      state: 'ACTIVE',
      label: email,
      sublabel: email,
      note: '',
      userId: null,
      email
    }));
    return [...users, ...emails];
  });

  constructor() {
    // Resuelve por `?ids=` los nombres de los userIds guardados en cuanto se monta el picker (y
    // cuando cambian, p. ej. al seleccionar otro nodo). El servicio agrupa las peticiones de
    // Para/CC/CCO en una sola.
    effect(() => {
      const ids = this.userIds();
      if (this.allowUsers() && ids.length) {
        this.directory.resolveUsers(ids);
      }
    });
  }

  protected chipClass(chip: RecipientChip): string {
    if (chip.kind === 'EMAIL') {
      return this.emailChipClass;
    }
    if (chip.state === 'INACTIVE') {
      return this.inactiveChipClass;
    }
    return chip.state === 'MISSING' ? this.missingChipClass : this.userChipClass;
  }

  protected chipIcon(chip: RecipientChip): string {
    if (chip.kind === 'EMAIL') {
      return 'fa-envelope';
    }
    return chip.state === 'ACTIVE' ? 'fa-user' : 'fa-user-slash';
  }

  protected chipTitle(chip: RecipientChip): string {
    if (chip.state === 'INACTIVE') {
      return `${chip.label}${chip.sublabel ? ' · ' + chip.sublabel : ''} — usuario inactivo: no recibirá el correo mientras esté dado de baja.`;
    }
    if (chip.state === 'MISSING') {
      return `${chip.label} — el directorio no devolvió este usuario: puede estar eliminado o pertenecer a otra empresa.`;
    }
    return chip.sublabel || chip.label;
  }

  // ── Usuarios del CRM ────────────────────────────────────────────────────────

  userSearch = (term: string): Observable<EntitySelectPage<DirectoryUser>> =>
    this.directory.searchUsers(term, 20).pipe(
      map((list) => ({ items: list.filter((u) => !this.userIds().includes(u.id)), totalPages: 1 })),
      catchError(() => of({ items: [] as DirectoryUser[], totalPages: 0 }))
    );
  userLabel = (user: DirectoryUser): string => directoryUserLabel(user);
  userSublabel = (user: DirectoryUser): string => user.mail ?? '';

  protected onUserSelected(user: DirectoryUser | null): void {
    if (!user || this.disabled() || this.userIds().includes(user.id)) {
      return;
    }
    this.directory.remember(user);
    this.userIdsChange.emit([...this.userIds(), user.id]);
    this.epoch.update((n) => n + 1);
  }

  // ── Correos sueltos ─────────────────────────────────────────────────────────

  /**
   * `(input)`, no `(change)`: el valor mostrado es un BUFFER local (aún no es config
   * persistida), pero necesita ir en vivo al signal para habilitar el botón "Añadir" y
   * limpiar el error mientras se corrige el correo. Nada de esto toca el store hasta pulsar
   * "Añadir" / Enter.
   */
  protected onEmailDraftInput(event: Event): void {
    this.emailDraft.set((event.target as HTMLInputElement).value);
    if (this.emailError()) {
      this.emailError.set('');
    }
  }

  protected onEmailEnter(event: Event): void {
    event.preventDefault();
    this.addEmails();
  }

  protected addEmails(): void {
    if (this.disabled()) {
      return;
    }
    const raw = this.emailDraft().trim();
    if (!raw) {
      return;
    }
    // Se admite pegar varios separados por coma, punto y coma o espacio.
    const parts = raw.split(/[;,\s]+/).filter(Boolean);
    const invalid = parts.filter((p) => !WORKFLOW_EMAIL_PATTERN.test(p));
    if (invalid.length) {
      this.emailError.set(
        invalid.length === 1
          ? `«${invalid[0]}» no es un correo válido. Escríbelo con el formato nombre@dominio.com.`
          : `Estos correos no son válidos: ${invalid.join(', ')}. Escríbelos con el formato nombre@dominio.com.`
      );
      return;
    }
    const current = this.emails();
    const existing = new Set(current.map((e) => e.toLowerCase()));
    const added: string[] = [];
    const duplicated: string[] = [];
    for (const part of parts) {
      const key = part.toLowerCase();
      if (existing.has(key)) {
        duplicated.push(part);
        continue;
      }
      existing.add(key);
      added.push(part);
    }
    if (!added.length) {
      this.emailError.set(
        duplicated.length === 1 ? `«${duplicated[0]}» ya está en la lista.` : 'Esos correos ya están en la lista.'
      );
      return;
    }
    this.emailsChange.emit([...current, ...added]);
    this.emailDraft.set('');
    this.emailError.set(
      duplicated.length ? `Ya estaban en la lista y no se repitieron: ${duplicated.join(', ')}.` : ''
    );
  }

  protected remove(chip: RecipientChip): void {
    if (this.disabled()) {
      return;
    }
    if (chip.kind === 'USER' && chip.userId !== null) {
      this.userIdsChange.emit(this.userIds().filter((id) => id !== chip.userId));
      return;
    }
    if (chip.email !== null) {
      const key = chip.email.toLowerCase();
      this.emailsChange.emit(this.emails().filter((e) => e.toLowerCase() !== key));
    }
  }
}
